import {
  ActiveOrdersContext,
  ActiveOrdersContextType,
} from '@/contexts/activeOrders/useActiveOrdersContext'
import useOrdersWebSocket from '@/contexts/ordersWebSocket/useOrdersWebSocket'
import {
  DEFAULT_SELLER_ACTIVE_ORDERS_VARIABLES,
  useActiveOrders,
} from '@/proxy/order/hooks'
import type { SellerActiveOrdersVariables } from '@/proxy/order/hooks/useActiveOrders/types'
import { Routes } from '@/resources/enums/enumRoutes'
import { FeatureToggle } from '@/resources/enums/featureToggle'
import { ActiveOrder } from '@/resources/types'
import {
  ACTIVE_ORDERS_SEARCH_DEBOUNCE_MS,
  filterActiveOrdersByKanbanCbeChip,
  hasKanbanFiltersAppliedToQuery,
  toActiveOrdersSearchParam,
} from '@/resources/utils/orders/activeOrdersKanbanFilter'
import { logger } from '@/monitoring/logger'
import { CHECK_ORDERS_POLLING_TIME } from '@/seller.application.polling.time.config'
import useFeatureToggle from '@/hooks/useFeatureToggle'
import { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router'

const getGraphqlRequestHttpStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return undefined
  }
  const response = (error as { response?: { status?: number } }).response
  return response?.status
}

const ActiveOrdersProvider = ({ children }: PropsWithChildren) => {
  const isSellerKanbanFilterEnabled =
    useFeatureToggle({ splitName: 'seller_kanban_filter' }) === FeatureToggle.ON

  const [kanbanFilterSearchInput, setKanbanFilterSearchInput] = useState('')
  const [debouncedSearchInput, setDebouncedSearchInput] = useState('')
  const [kanbanFilterDeliveryType, setKanbanFilterDeliveryType] = useState<
    number | null
  >(null)
  const [kanbanFilterCbeChipId, setKanbanFilterCbeChipId] = useState<
    string | null
  >(null)

  const { orders, isWebSocketActive, hasUpdatedOrders } = useOrdersWebSocket()
  const prevWasWebSocketActiveRef = useRef(false)
  const prevWasWebSocketFeedActiveRef = useRef(false)
  const { pathname } = useLocation()
  const isActiveOrdersRoute =
    pathname === (Routes.ORDERS as string) ||
    pathname === (Routes.ORDERS_MAP as string)
  const isNanoDropRoute = pathname === (Routes.NANO_DROP as string)

  useEffect(() => {
    const onOrdersSection =
      pathname === (Routes.ORDERS as string) ||
      pathname === (Routes.ORDERS_MAP as string) ||
      pathname === (Routes.NANO_DROP as string)
    if (!onOrdersSection) {
      setKanbanFilterSearchInput('')
      setDebouncedSearchInput('')
      setKanbanFilterDeliveryType(null)
      setKanbanFilterCbeChipId(null)
    }
  }, [pathname])

  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearchInput(kanbanFilterSearchInput)
    }, ACTIVE_ORDERS_SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(id)
  }, [kanbanFilterSearchInput])

  const sellerActiveOrdersVariables: SellerActiveOrdersVariables =
    useMemo(() => {
      if (!isSellerKanbanFilterEnabled) {
        return DEFAULT_SELLER_ACTIVE_ORDERS_VARIABLES
      }
      return {
        search: toActiveOrdersSearchParam(debouncedSearchInput),
        deliveryType: kanbanFilterDeliveryType,
      }
    }, [
      isSellerKanbanFilterEnabled,
      debouncedSearchInput,
      kanbanFilterDeliveryType,
    ])

  const isKanbanFilterSearchDebouncing =
    isSellerKanbanFilterEnabled &&
    kanbanFilterSearchInput !== debouncedSearchInput

  const kanbanFiltersAppliedToQuery = useMemo(
    () =>
      hasKanbanFiltersAppliedToQuery(
        isSellerKanbanFilterEnabled,
        debouncedSearchInput,
        kanbanFilterDeliveryType
      ),
    [
      debouncedSearchInput,
      isSellerKanbanFilterEnabled,
      kanbanFilterDeliveryType,
    ]
  )

  const isWebSocketFeatureEnabled =
    useFeatureToggle({ splitName: 'seller_enable_wss' }) === FeatureToggle.ON

  const isWebSocketFeedActive = Boolean(
    isWebSocketFeatureEnabled &&
    isWebSocketActive &&
    hasUpdatedOrders &&
    !kanbanFiltersAppliedToQuery
  )

  useEffect(() => {
    const wasWebSocketFeedActive = prevWasWebSocketFeedActiveRef.current
    prevWasWebSocketFeedActiveRef.current = isWebSocketFeedActive

    if (
      wasWebSocketFeedActive &&
      !isWebSocketFeedActive &&
      isWebSocketFeatureEnabled &&
      !kanbanFiltersAppliedToQuery
    ) {
      logger.info('active_orders_web_socket_fallback_to_polling', {
        event: 'active_orders_web_socket_fallback_to_polling',
      })
    }
  }, [
    isWebSocketFeedActive,
    isWebSocketFeatureEnabled,
    kanbanFiltersAppliedToQuery,
  ])

  const isGraphqlPollingPaused = isWebSocketFeedActive

  const isQueryDisabled = isWebSocketFeedActive
  const shouldFetchActiveOrders = !isNanoDropRoute && !isQueryDisabled

  const {
    isFetching: isQueryFetching,
    isFetched: isQueryFetched,
    data: { activeOrders: queryOrders = [], error: queryError } = {},
    error: queryRequestError,
    isError: isQueryRequestError,
    refetch,
  } = useActiveOrders(
    {
      gcTime: 0,
      refetchInterval: isGraphqlPollingPaused
        ? false
        : CHECK_ORDERS_POLLING_TIME,
      refetchIntervalInBackground: true,
      enabled: shouldFetchActiveOrders,
    },
    !isActiveOrdersRoute && !isNanoDropRoute,
    sellerActiveOrdersVariables
  )

  useEffect(() => {
    if (
      !isWebSocketActive &&
      isWebSocketActive !== prevWasWebSocketActiveRef.current
    ) {
      void refetch()
    }
    prevWasWebSocketActiveRef.current = isWebSocketActive
  }, [isWebSocketActive, refetch])

  const activeOrders: ActiveOrder[] = useMemo(() => {
    let sourceOrders: ActiveOrder[]

    if (kanbanFiltersAppliedToQuery) {
      sourceOrders = queryOrders
    } else if (isWebSocketFeedActive) {
      logger.info('[WebSocket] Returning orders from WebSocket.')
      sourceOrders = orders
    } else {
      sourceOrders = queryOrders
    }

    return filterActiveOrdersByKanbanCbeChip(
      sourceOrders,
      kanbanFilterCbeChipId
    )
  }, [
    kanbanFiltersAppliedToQuery,
    isWebSocketFeedActive,
    orders,
    queryOrders,
    kanbanFilterCbeChipId,
  ])

  const httpStatus = getGraphqlRequestHttpStatus(queryRequestError)
  const isActiveOrdersQueryError =
    isQueryRequestError && (httpStatus === 500 || httpStatus === 503)

  const context: ActiveOrdersContextType = useMemo(
    () => ({
      activeOrders,
      isPollingActive: !isGraphqlPollingPaused,
      isFromWebSocket: isWebSocketFeedActive,
      isQueryFetching,
      isFetched: kanbanFiltersAppliedToQuery
        ? isQueryFetched
        : isQueryFetched || (isWebSocketFeatureEnabled && hasUpdatedOrders),
      queryError,
      isActiveOrdersQueryError,
      isSellerKanbanFilterEnabled,
      kanbanFiltersAppliedToQuery,
      kanbanFilterSearchInput,
      setKanbanFilterSearchInput,
      kanbanFilterDeliveryType,
      setKanbanFilterDeliveryType,
      kanbanFilterCbeChipId,
      setKanbanFilterCbeChipId,
      isKanbanFilterSearchDebouncing,
    }),
    [
      activeOrders,
      isGraphqlPollingPaused,
      isWebSocketFeedActive,
      isQueryFetching,
      queryError,
      isQueryFetched,
      isActiveOrdersQueryError,
      isSellerKanbanFilterEnabled,
      kanbanFiltersAppliedToQuery,
      isWebSocketFeatureEnabled,
      hasUpdatedOrders,
      kanbanFilterSearchInput,
      kanbanFilterDeliveryType,
      kanbanFilterCbeChipId,
      isKanbanFilterSearchDebouncing,
    ]
  )

  return (
    <ActiveOrdersContext.Provider value={context}>
      {children}
    </ActiveOrdersContext.Provider>
  )
}

export default ActiveOrdersProvider
